import React, { useEffect, useRef, useState } from "react";
import AuthLayout from "../../layouts/AuthLayout";
import ButtonLong from "../../components/ButtonLong";
import useAuthStore from "../../store/auth";
import TextInput from "../../components/TextInput";
import CheckBox from "../../components/CheckBox";
import ReCAPTCHA from "react-google-recaptcha";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { apollo } from "../../utils/apollo";
import { LOGIN } from "../../utils/graphql";
import { toast } from "react-toastify";

function Login() {
  const { t } = useTranslation();
  const { token, setUserAuth } = useAuthStore();
  const recaptcha = useRef();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.invalidateQueries();
  }, [queryClient]);

  useEffect(() => {
    if (token) navigate("/dashboard");
  }, [token, navigate]);

  const loginResetMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apollo.mutate({
        mutation: LOGIN,
        variables: {
          input: {
            email,
            password,
            captcha: recaptcha.current?.getValue(),
            rememberMe,
          },
        },
      });
      return data?.login;
    },
    onError: (error) => {
      if (error.graphQLErrors) {
        error.graphQLErrors.map((item) => console.log(item));
      }
      recaptcha.current?.reset();
      setLoading(false);
    },
    onSuccess: async (data) => {
      setLoading(true);
      if (data.token) {
        setUserAuth({
          token: data.token,
          user: data.user,
          company: data.company,
        });
      }
    },
    onSettled: async () => {
      setLoading(false);
      recaptcha?.current?.reset();
    },
  });

  const loginHandler = async (e) => {
    e.preventDefault();
    const captcha = recaptcha.current?.getValue();
    if (!captcha) {
      toast.error("Please verify the reCAPTCHA!");
      return;
    }
    loginResetMutation.mutate();
  };

  return (
    <AuthLayout title={t("auth.login_page_title")}>
      <form className="space-y-6" method="POST">
        <TextInput
          label={t("auth.email_address")}
          get={email}
          set={setEmail}
          id="email"
          name="email"
          type="email"
          required={true}
        />
        <TextInput
          label={t("auth.password")}
          get={password}
          set={setPassword}
          id="password"
          name="password"
          type="password"
          required={true}
        />

        <div className="flex items-center justify-between">
          <CheckBox
            label={t("auth.remember_me")}
            get={rememberMe}
            set={setRememberMe}
            id="remember-me"
            name="remember-me"
            type="checkbox"
          />
          <div className="text-sm leading-6">
            <Link
              to="/reset-password"
              className="font-semibold text-primary-600 hover:text-primary-500 select-none"
            >
              {t("auth.forgot_password")}
            </Link>
          </div>
        </div>

        <div className="flex justify-center">
          <ReCAPTCHA ref={recaptcha} sitekey={process.env.REACT_APP_SITE_KEY} />
        </div>
        <div>
          <ButtonLong type="submit" onClick={loginHandler} disabled={loading}>
            {t("auth.sign_in")}
          </ButtonLong>
        </div>
      </form>
    </AuthLayout>
  );
}

export default Login;
